import type { SupabaseClient } from "@supabase/supabase-js";

function sevenDaysFromNow(): string {
  return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
}

export interface UpsertInvitationFields {
  role: string;
  phone?: string | null;
  email?: string | null;
  full_name?: string | null;
  custom_role?: string | null;
  invited_by?: string;
  related_user_type?: string;
  related_user_id?: string | null;
  property_id?: string | null;
  unit_id?: string | null;
  role_permission_id?: string | null;
  [key: string]: unknown;
}

/**
 * Upsert an invitation, scoped by role and matched on whichever identifiers
 * (phone, email) are populated.
 *
 * The DB enforces two partial unique indexes for status='pending':
 *   • UNIQUE(company_id, phone, role)         WHERE phone IS NOT NULL
 *   • UNIQUE(company_id, lower(email), role)  WHERE email IS NOT NULL
 *
 * This function mirrors that policy in the application:
 *   • Looks up an existing non-cancelled invite for the same role where EITHER
 *     phone OR email matches.
 *   • If two different rows match (phone hits one, email hits another) — throws
 *     and asks the admin to clean up first.
 *   • If the matched row is `accepted` — throws "already accepted as this role".
 *   • If the matched row is `pending` — refreshes it (new token + expiry).
 *   • Otherwise inserts a new pending invite.
 *
 * Phone is the canonical identifier; email is optional. At least one must be
 * provided.
 */
export async function upsertInvitation(
  client: SupabaseClient,
  companyId: string,
  fields: UpsertInvitationFields,
): Promise<{ id: string; token: string }> {
  const role = fields.role;
  const phone = fields.phone ? String(fields.phone) : null;
  const email = fields.email ? String(fields.email).toLowerCase() : null;

  if (!phone && !email) {
    throw new Error(
      "رقم الهاتف أو البريد الإلكتروني مطلوب | Phone or email is required",
    );
  }

  const orParts: string[] = [];
  if (phone) orParts.push(`phone.eq.${phone}`);
  if (email) orParts.push(`email.eq.${email}`);

  const { data: matches, error: lookupError } = await client
    .from("invitations")
    .select("id, token, status, phone, email")
    .eq("company_id", companyId)
    .eq("role", role)
    .neq("status", "cancelled")
    .or(orParts.join(","));

  if (lookupError) throw lookupError;

  if (matches && matches.length > 1) {
    throw new Error(
      "هناك دعوات قائمة بنفس رقم الهاتف أو البريد الإلكتروني — يرجى إلغاؤها أولاً | Found multiple existing invitations matching this phone or email — please cancel them first",
    );
  }

  // Normalize email to lowercase for storage too (matches the partial index).
  const normalizedFields = { ...fields, email };

  if (matches && matches.length === 1) {
    const existing = matches[0] as { id: string; status: string };
    if (existing.status === "accepted") {
      throw new Error(
        "هذا المستخدم قبل الدعوة بالفعل بهذا الدور ولا يمكن إعادة دعوته | This user has already accepted the invitation for this role and cannot be re-invited",
      );
    }

    // Pending: refresh the existing row — but KEEP the existing token so any
    // link already sent stays valid. Re-sending an invite must NOT invalidate
    // the recipient's existing link: regenerating the token here was the
    // "expired invite" trap (a resend silently killed the link the person
    // already had). We only extend the expiry window; the token (and thus the
    // invite URL) is stable across resends. To revoke a link, cancel + re-invite.
    const { data, error } = await client
      .from("invitations")
      .update({
        ...normalizedFields,
        status: "pending",
        expires_at: sevenDaysFromNow(),
      })
      .eq("id", existing.id)
      .eq("company_id", companyId)
      .select("id, token")
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error("Invitation not found or access denied");
    return data as { id: string; token: string };
  }

  // No existing match — insert a new pending invitation.
  const { data, error } = await client
    .from("invitations")
    .insert({
      company_id: companyId,
      ...normalizedFields,
      status: "pending",
      expires_at: sevenDaysFromNow(),
    })
    .select("id, token")
    .single();
  if (error) throw error;
  return data as { id: string; token: string };
}
